import { useEffect, useState } from 'react';
import { useApi, type Ticket } from '@/lib/api';
import { useMe } from '@/lib/useMe';
import { MeGate } from '@/components/MeGate';
import { CoreIcon } from '@/components/CoreIcon';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

interface Customer {
  email: string;
  ticket_count: number;
  open_count: number;
  resolved_count: number;
  last_seen: number;
  first_seen: number;
}

export function Customers() {
  const meStatus = useMe();
  return <MeGate status={meStatus}>{(me) => <CustomersBody orgId={me.org!.id} />}</MeGate>;
}

function CustomersBody({ orgId }: { orgId: string }) {
  const api = useApi();
  const nav = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Customer | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [memory, setMemory] = useState<{
    facts: Array<{ key: string; value: string }>;
    summary: string | null;
    sentiment: string | null;
    ticket_count: number;
  } | null>(null);

  useEffect(() => {
    setLoading(true);
    api.get<{ customers: Customer[] }>(`/api/orgs/${orgId}/customers`)
      .then((r) => setCustomers(r.customers))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [api, orgId]);

  useEffect(() => {
    if (!selected) { setTickets([]); setMemory(null); return; }
    setTicketsLoading(true);
    api.get<{ tickets: Ticket[] }>(`/api/orgs/${orgId}/customers/${encodeURIComponent(selected.email)}/tickets`)
      .then((r) => setTickets(r.tickets))
      .catch(console.error)
      .finally(() => setTicketsLoading(false));
    api.get<{ memory: typeof memory }>(`/api/orgs/${orgId}/customers/${encodeURIComponent(selected.email)}/memory`)
      .then((r) => setMemory(r.memory))
      .catch(() => setMemory(null));
  }, [api, orgId, selected]);

  const filtered = query
    ? customers.filter((c) => c.email.toLowerCase().includes(query.toLowerCase()))
    : customers;

  return (
    <div className="h-full grid grid-cols-[340px_minmax(0,1fr)] bg-background">
      {/* ── Customer list ── */}
      <aside className="border-r border-border flex flex-col min-h-0">
        <header className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-[15px] font-semibold">Customers</h1>
            <span className="text-[11px] text-muted-foreground tabular-nums">
              {filtered.length}
            </span>
          </div>
          <div className="relative">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
              <CoreIcon name="search" size={14} />
            </span>
            <input
              type="search"
              placeholder="Search by email…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="input !h-8 !pl-8 !text-[13px]"
            />
          </div>
        </header>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <LoadingRows />
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              {query ? 'No customers match your search.' : 'No customers yet. Tickets create customer records automatically.'}
            </div>
          ) : (
            filtered.map((c) => (
              <button
                key={c.email}
                type="button"
                onClick={() => setSelected(c)}
                className={cn(
                  'w-full text-left px-4 py-3 border-b border-border transition-colors',
                  selected?.email === c.email
                    ? 'bg-muted/60'
                    : 'hover:bg-muted/30',
                )}
              >
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-foreground text-surface grid place-items-center text-[11px] font-semibold uppercase shrink-0">
                    {c.email.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium truncate">{c.email}</div>
                    <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-0.5">
                      <span>{c.ticket_count} ticket{c.ticket_count !== 1 ? 's' : ''}</span>
                      {c.open_count > 0 && (
                        <span className="text-accent">{c.open_count} open</span>
                      )}
                    </div>
                  </div>
                  <span className="text-[10px] text-muted-foreground shrink-0">
                    {relativeTime(c.last_seen)}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>
      </aside>

      {/* ── Customer detail ── */}
      <section className="flex flex-col min-h-0 overflow-y-auto">
        {selected ? (
          <div className="flex-1">
            <header className="p-6 border-b border-border bg-surface">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-foreground text-surface grid place-items-center text-lg font-semibold uppercase shrink-0">
                  {selected.email.charAt(0)}
                </div>
                <div>
                  <h2 className="text-lg font-semibold">{selected.email}</h2>
                  <div className="flex items-center gap-4 text-[12px] text-muted-foreground mt-1">
                    <span>First seen {formatDate(selected.first_seen)}</span>
                    <span>Last active {relativeTime(selected.last_seen)}</span>
                  </div>
                </div>
              </div>
            </header>

            {/* Stats row */}
            <div className="grid grid-cols-4 gap-4 p-6">
              <StatCard label="Total tickets" value={selected.ticket_count} />
              <StatCard label="Open" value={selected.open_count} accent={selected.open_count > 0} />
              <StatCard label="Resolved" value={selected.resolved_count} />
              <StatCard
                label="Resolution rate"
                value={selected.ticket_count > 0
                  ? `${Math.round((selected.resolved_count / selected.ticket_count) * 100)}%`
                  : '—'}
              />
            </div>

            {/* AI Memory */}
            {memory && (
              <div className="px-6 pb-2">
                <div className="card p-4">
                  <div className="mono-label mb-3 flex items-center gap-1.5">
                    <CoreIcon name="lightbulb" size={12} />
                    AI Memory
                  </div>
                  {memory.sentiment && (
                    <div className="flex items-center gap-2 text-[12px] mb-2">
                      <span className="text-muted-foreground">Sentiment:</span>
                      <span className={cn(
                        'capitalize font-medium',
                        memory.sentiment === 'positive' && 'text-success-foreground',
                        memory.sentiment === 'negative' && 'text-destructive',
                        memory.sentiment === 'frustrated' && 'text-accent',
                      )}>{memory.sentiment}</span>
                    </div>
                  )}
                  {memory.summary && (
                    <p className="text-[12px] text-muted-foreground mb-3">{memory.summary}</p>
                  )}
                  {memory.facts?.length > 0 && (
                    <div className="space-y-1.5">
                      {memory.facts.map((f, i) => (
                        <div key={i} className="flex items-start gap-2 text-[12px]">
                          <span className="text-muted-foreground shrink-0">{f.key}:</span>
                          <span>{f.value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Ticket timeline */}
            <div className="px-6 pb-6">
              <div className="mono-label mb-3 flex items-center gap-1.5">
                <CoreIcon name="history" size={12} />
                Ticket history
              </div>
              {ticketsLoading ? (
                <LoadingRows />
              ) : tickets.length === 0 ? (
                <p className="text-sm text-muted-foreground">No tickets found.</p>
              ) : (
                <div className="space-y-2">
                  {tickets.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => nav(`/app/inbox/${encodeURIComponent(t.id)}`)}
                      className="card w-full text-left p-3 hover:border-foreground/30 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <StatusDot status={t.status} />
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-medium truncate">
                            {t.subject || '(no subject)'}
                          </div>
                          <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-1">
                            <span className="capitalize">{t.status.replace('_', ' ')}</span>
                            {t.ai_category && <span>{t.ai_category}</span>}
                            <span className="capitalize">{t.priority}</span>
                            <span>{relativeTime(t.last_activity)}</span>
                          </div>
                        </div>
                        {t.ai_confidence !== null && (
                          <span className="text-[11px] text-muted-foreground tabular-nums shrink-0">
                            {Math.round(t.ai_confidence * 100)}%
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="grid place-items-center h-full text-muted-foreground">
            <div className="text-center max-w-sm p-8">
              <div className="mx-auto mb-4 h-12 w-12 rounded-lg border border-border bg-surface grid place-items-center">
                <CoreIcon name="people" size={20} />
              </div>
              <h3 className="text-foreground font-medium text-[15px]">Select a customer</h3>
              <p className="text-sm mt-1">
                Pick a customer on the left to see their full ticket history and engagement.
              </p>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function StatCard({ label, value, accent }: { label: string; value: string | number; accent?: boolean }) {
  return (
    <div className="card p-3">
      <div className="mono-label mb-1">{label}</div>
      <div className={cn('text-xl font-semibold tabular-nums', accent && 'text-accent')}>
        {value}
      </div>
    </div>
  );
}

function StatusDot({ status }: { status: string }) {
  const color =
    status === 'open' ? 'bg-accent'
    : status === 'awaiting_approval' ? 'bg-warning'
    : status === 'resolved' ? 'bg-success'
    : 'bg-muted-foreground';
  return <span className={cn('mt-1.5 h-2 w-2 rounded-full shrink-0', color)} />;
}

function LoadingRows() {
  return (
    <div className="p-4 space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="animate-pulse flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-muted" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 bg-muted rounded w-3/4" />
            <div className="h-2.5 bg-muted rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(ts);
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
